import { ROUTE_BASE_NAME } from '~/constants'
/**
 * Composable that returns the current route base name. Previously this was a
 * direct call in i18n, but since the Nuxt3 version of this module, we always
 * need to get the route and pass it into their composable. We use it in a lot
 * of places, so this abstraction makes sense.
 */
export default function useBaseName() {
  const router = useRouter()
  const getRouteBaseName = useRouteBaseName()
  const renderedRoute = useRenderedRoute()

  const routeBaseName = computed(() => {
    return getRouteBaseName(router.currentRoute.value)?.toString() ?? ''
  })

  function checkRouteNameStartsWith(route: string, name: (typeof ROUTE_BASE_NAME)[keyof typeof ROUTE_BASE_NAME]) {
    return route.startsWith(name)
  }

  /**
   * A collection of route-only checks.
   *
   * Be careful: this does NOT check if the page is fully loaded and is only a
   * check against the router path!
   */
  const isRouteHomePage = computed(() => {
    return checkRouteNameStartsWith(routeBaseName.value, ROUTE_BASE_NAME.index)
  })

  const isRouteOwnerPage = computed(() => {
    return checkRouteNameStartsWith(routeBaseName.value, ROUTE_BASE_NAME.owner)
  })

  const isRouteRvSearchPage = computed(() => {
    return checkRouteNameStartsWith(routeBaseName.value, ROUTE_BASE_NAME.rv_search)
  })

  const isRouteRvRentalPage = computed(() => {
    return checkRouteNameStartsWith(routeBaseName.value, ROUTE_BASE_NAME.rv_rental)
  })

  const isRouteRvRentalsPage = computed(() => {
    return checkRouteNameStartsWith(routeBaseName.value, ROUTE_BASE_NAME.rv_rentals_base)
  })

  const isRouteRvRentalsOrChildPage = computed(() => {
    return checkRouteNameStartsWith(routeBaseName.value, ROUTE_BASE_NAME.rv_rentals)
  })

  const isRouteRequestToBookPage = computed(() => {
    return checkRouteNameStartsWith(routeBaseName.value, ROUTE_BASE_NAME.rv_rental_request_to_book_alias) || checkRouteNameStartsWith(routeBaseName.value, ROUTE_BASE_NAME.request_to_book)
  })

  const isRoutePaymentPage = computed(() => {
    return checkRouteNameStartsWith(routeBaseName.value, ROUTE_BASE_NAME.payment)
  })

  const isRouteRequestToBookFlow = computed(() => {
    return (isRouteRequestToBookPage.value || isRoutePaymentPage.value)
  })

  const isRouteMessagesArchivesPage = computed(() => {
    return checkRouteNameStartsWith(routeBaseName.value, ROUTE_BASE_NAME.messages_archives)
  })

  const isRouteCampgroundSearchPage = computed(() => {
    return checkRouteNameStartsWith(routeBaseName.value, ROUTE_BASE_NAME.campgrounds_search)
  })

  const isRouteRvRentalsCampgroundPage = computed(() => {
    return checkRouteNameStartsWith(routeBaseName.value, ROUTE_BASE_NAME.rv_rentals_campground)
  })

  const isRouteRvRentalsParkPage = computed(() => {
    return checkRouteNameStartsWith(routeBaseName.value, ROUTE_BASE_NAME.rv_rentals_park)
  })

  const isRouteRvRentalsEventPage = computed(() => {
    return checkRouteNameStartsWith(routeBaseName.value, ROUTE_BASE_NAME.rv_rentals_event)
  })

  const isRouteRvRentalsDestinationPage = computed(() => {
    return checkRouteNameStartsWith(routeBaseName.value, ROUTE_BASE_NAME.rv_rentals_destination)
  })

  /**
   * A collection of data-loading dependent route checks.
   *
   * Note: these checks wait for the page to be fully resolved and are usually
   * the most appropriate variables to use when conditionally rendering
   * elements.
   */
  const isRenderedHomePage = computed(() => {
    return checkRouteNameStartsWith(renderedRoute.value, ROUTE_BASE_NAME.index)
  })

  const isRenderedOwnerPage = computed(() => {
    return checkRouteNameStartsWith(renderedRoute.value, ROUTE_BASE_NAME.owner)
  })

  const isRenderedRvSearchPage = computed(() => {
    return checkRouteNameStartsWith(renderedRoute.value, ROUTE_BASE_NAME.rv_search)
  })

  const isRenderedRvRentalPage = computed(() => {
    return checkRouteNameStartsWith(renderedRoute.value, ROUTE_BASE_NAME.rv_rental)
  })

  const isRenderedRvRentalsPage = computed(() => {
    return checkRouteNameStartsWith(renderedRoute.value, ROUTE_BASE_NAME.rv_rentals_base)
  })

  const isRenderedRvRentalsOrChildPage = computed(() => {
    return checkRouteNameStartsWith(renderedRoute.value, ROUTE_BASE_NAME.rv_rentals)
  })

  const isRenderedRequestToBookPage = computed(() => {
    return checkRouteNameStartsWith(renderedRoute.value, ROUTE_BASE_NAME.rv_rental_request_to_book_alias)
  })

  const isRenderedPaymentPage = computed(() => {
    return checkRouteNameStartsWith(renderedRoute.value, ROUTE_BASE_NAME.payment)
  })

  const isRenderedRequestToBookFlow = computed(() => {
    return isRenderedRequestToBookPage.value || isRenderedPaymentPage.value
  })

  const isRenderedMessagesArchivesPage = computed(() => {
    return checkRouteNameStartsWith(renderedRoute.value, ROUTE_BASE_NAME.messages_archives)
  })

  const isRenderedReferralPage = computed(() => {
    return checkRouteNameStartsWith(renderedRoute.value, ROUTE_BASE_NAME.referral)
  })

  const isRenderedCampgroundSearchPage = computed(() => {
    return checkRouteNameStartsWith(renderedRoute.value, ROUTE_BASE_NAME.campgrounds_search)
  })

  const isRenderedRvRentalsCampgroundPage = computed(() => {
    return checkRouteNameStartsWith(renderedRoute.value, ROUTE_BASE_NAME.rv_rentals_campground)
  })

  const isRenderedRvRentalsParkPage = computed(() => {
    return checkRouteNameStartsWith(renderedRoute.value, ROUTE_BASE_NAME.rv_rentals_park)
  })

  const isRenderedRvRentalsEventPage = computed(() => {
    return checkRouteNameStartsWith(renderedRoute.value, ROUTE_BASE_NAME.rv_rentals_event)
  })

  const isRenderedRvRentalsDestinationPage = computed(() => {
    return checkRouteNameStartsWith(renderedRoute.value, ROUTE_BASE_NAME.rv_rentals_destination)
  })

  const isRenderedCustomerServicePage = computed(() => {
    return checkRouteNameStartsWith(renderedRoute.value, ROUTE_BASE_NAME.customer_service)
  })

  const isRenderedDashboardPage = computed(() => {
    return checkRouteNameStartsWith(renderedRoute.value, ROUTE_BASE_NAME.dashboard)
  })

  /**
   * A collection of mode-based checks.
   */
  const dashboardMode = computed(() => {
    return router.currentRoute.value.params?.dashboard ?? 'dashboard'
  })

  const isHostMode = computed(() => {
    return dashboardMode.value === 'host'
  })

  async function hostModeUrlRewrite() {
    const { userIsBookingOwner, userOwner } = useBookingUsers()

    if (!isHostMode.value && (!userOwner.value?.userId || userIsBookingOwner.value)) {
      await navigateTo({ params: { dashboard: 'host' }, query: router.currentRoute.value.query, replace: true })
      return true
    }

    return false
  }

  return {
    routeBaseName,

    isRouteHomePage,
    isRouteMessagesArchivesPage,
    isRouteOwnerPage,
    isRoutePaymentPage,
    isRouteRequestToBookPage,
    isRouteRequestToBookFlow,
    isRouteRvRentalPage,
    isRouteRvRentalsOrChildPage,
    isRouteRvRentalsPage,
    isRouteRvSearchPage,
    isRouteCampgroundSearchPage,
    isRouteRvRentalsCampgroundPage,
    isRouteRvRentalsParkPage,
    isRouteRvRentalsEventPage,
    isRouteRvRentalsDestinationPage,

    isRenderedHomePage,
    isRenderedOwnerPage,
    isRenderedPaymentPage,
    isRenderedRequestToBookFlow,
    isRenderedRequestToBookPage,
    isRenderedRvRentalPage,
    isRenderedRvRentalsOrChildPage,
    isRenderedRvRentalsPage,
    isRenderedRvSearchPage,
    isRenderedMessagesArchivesPage,
    isRenderedReferralPage,
    isRenderedCampgroundSearchPage,
    isRenderedRvRentalsCampgroundPage,
    isRenderedRvRentalsParkPage,
    isRenderedRvRentalsEventPage,
    isRenderedRvRentalsDestinationPage,
    isRenderedCustomerServicePage,
    isRenderedDashboardPage,

    dashboardMode,
    isHostMode,

    hostModeUrlRewrite,
    checkRouteNameStartsWith,
  }
}
